import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { X, Pencil, Clock, MapPin, Monitor, AlertTriangle, User, Users, Package, Truck, CheckCircle2, XCircle, Film, Loader2 } from "lucide-react";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/perms";
import { fmtDate, fmtDateOnly } from "@/lib/time";
import api, { apiError } from "@/lib/api";
import { TICKET_STATUS, statusMeta, categoryMeta, prioMeta, typeMeta, isOverdue, assigneeLabel, slaState } from "@/lib/tickets";

function Row({ label, children }) {
  return (
    <div className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0">
      <div className="w-32 shrink-0 text-[11px] font-mono uppercase tracking-wider text-gray-600 pt-0.5">{label}</div>
      <div className="flex-1 min-w-0 text-sm text-gray-800">{children}</div>
    </div>
  );
}

function SlaBadge({ label, due, breached, closed, de }) {
  const st = slaState(due, breached, closed);
  const cls = st?.level === "breached" ? "border-red-500/40 bg-red-500/15 text-red-700"
    : st?.level === "soon" ? "border-amber-500/40 bg-amber-500/15 text-amber-700"
    : "border-[#c00000]/30 bg-[#c00000]/10 text-[#c00000]";
  const txt = st?.level === "breached" ? (de ? "überschritten" : "breached")
    : st?.level === "soon" ? (de ? "läuft bald ab" : "due soon")
    : closed ? (de ? "eingehalten" : "met") : (de ? "im Plan" : "on track");
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded border font-mono w-fit ${cls}`} data-testid={`sla-badge-${label}`}>
      {st?.level === "breached" && <AlertTriangle className="w-3 h-3" />}<Clock className="w-3 h-3" />
      <span className="uppercase">{label}</span> · {fmtDate(due)} · {txt}
    </span>
  );
}

function AttachmentThumb({ att }) {
  const [url, setUrl] = useState("");
  const [err, setErr] = useState(false);
  const [open, setOpen] = useState(false);
  const isVideo = (att.content_type || "").startsWith("video/");
  useEffect(() => {
    let alive = true; let obj = "";
    api.get(`/attachments/${att.id}`, { responseType: "blob" })
      .then((r) => { if (!alive) return; obj = URL.createObjectURL(r.data); setUrl(obj); })
      .catch(() => { if (alive) setErr(true); });
    return () => { alive = false; if (obj) URL.revokeObjectURL(obj); };
  }, [att.id]);
  return (
    <>
      <button data-testid={`ticket-attach-${att.id}`} onClick={() => url && setOpen(true)} title={att.filename}
        className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200 bg-black/30 flex items-center justify-center hover:border-[#c00000]/50 transition">
        {err ? <span className="text-[9px] text-red-600 px-1">Fehler</span>
          : !url ? <Loader2 className="w-4 h-4 animate-spin text-gray-600" />
          : isVideo ? <><video src={url} className="w-full h-full object-cover" /><Film className="absolute w-5 h-5 text-gray-900 drop-shadow" /></>
          : <img src={url} alt={att.filename} className="w-full h-full object-cover" />}
      </button>
      {open && url && (
        <div className="fixed inset-0 z-[90] bg-black/85 flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid="ticket-attach-viewer">
          <button className="absolute top-4 right-4 w-9 h-9 rounded-full bg-gray-100 border border-gray-300 text-gray-900 flex items-center justify-center hover:bg-gray-200" onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
          {isVideo
            ? <video src={url} controls autoPlay className="max-w-full max-h-[85vh] rounded-lg" onClick={(e) => e.stopPropagation()} />
            : <img src={url} alt={att.filename} className="max-w-full max-h-[85vh] rounded-lg" onClick={(e) => e.stopPropagation()} />}
        </div>
      )}
    </>
  );
}

export default function TicketDetailDialog({ ticket, users = [], locName = {}, onClose, onEdit, onChanged }) {
  const { lang } = useLang();
  const de = lang === "de";
  const { user } = useAuth();
  const canEdit = can(user, "tickets", "edit");
  const canApprove = can(user, "freigabe", "edit");
  const navigate = useNavigate();
  if (!ticket) return null;
  const t = ticket;
  const overdue = isOverdue(t);
  const cat = categoryMeta(t.category);
  const pm = prioMeta(t.priority);
  const sm = statusMeta(t.status);
  const dash = <span className="text-gray-600">—</span>;

  const setStatus = async (status) => {
    try { await api.patch(`/tickets/${t.id}`, { status }); window.dispatchEvent(new Event("tickets:changed")); onChanged && onChanged(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const decide = async (ok) => {
    try { await api.post(`/tickets/${t.id}/${ok ? "approve" : "reject"}`); window.dispatchEvent(new Event("tickets:changed")); onChanged && onChanged(); onClose && onClose(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const go = (path) => { onClose && onClose(); navigate(path); };
  const shipLabel = { versendet: de ? "versendet" : "shipped", zugestellt: de ? "zugestellt" : "delivered", erhalten: de ? "erhalten" : "received", offen: de ? "offen" : "open" };

  return (
    <>
      <div className="fixed inset-0 bg-black/60 z-[60]" onClick={onClose} />
      <div data-testid="ticket-detail-dialog" className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[70] w-[calc(100vw-2rem)] max-w-2xl max-h-[88vh] overflow-y-auto bg-gray-50 border border-gray-200 rounded-2xl shadow-2xl">
        <div className="flex items-start justify-between gap-3 p-5 border-b border-gray-200 sticky top-0 bg-gray-50 z-10">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border ${sm.cls}`}>{de ? sm.de : sm.en}</span>
              <span data-testid="ticket-detail-type" className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border ${typeMeta(t.ticket_type).cls}`}>{de ? typeMeta(t.ticket_type).de : typeMeta(t.ticket_type).en}</span>
              {t.approval_status === "pending" && <span data-testid="ticket-approval-pending" className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border border-amber-500/40 bg-amber-500/15 text-amber-700">{de ? "Freigabe offen" : "Approval pending"}</span>}
              {t.approval_status === "approved" && <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border border-[#c00000]/40 bg-[#c00000]/15 text-[#c00000]">{de ? "Freigegeben" : "Approved"}</span>}
              {t.approval_status === "rejected" && <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border border-red-500/40 bg-red-500/15 text-red-700">{de ? "Abgelehnt" : "Rejected"}</span>}
              {t.source === "portal" && <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border border-[#c00000]/30 bg-[#c00000]/10 text-[#c00000]">Portal</span>}
              <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border ${pm.cls}`}>{de ? pm.de : pm.en}</span>
              {overdue && <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border border-red-500/40 bg-red-500/15 text-red-700"><AlertTriangle className="w-3 h-3" /> {de ? "überfällig" : "overdue"}</span>}
            </div>
            <h2 className="text-lg font-bold text-gray-900 mt-2 break-words">{t.title}</h2>
          </div>
          <button data-testid="ticket-detail-close" onClick={onClose} className="shrink-0 p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5">
          <Row label="Status">
            <select data-testid="ticket-detail-status" value={t.status} disabled={!canEdit} onChange={(e) => setStatus(e.target.value)}
              className={`text-xs font-mono rounded border px-2 py-1 focus:outline-none disabled:opacity-70 ${sm.cls}`}>
              {TICKET_STATUS.map((s) => <option key={s.id} value={s.id} className="bg-gray-50 text-gray-900">{de ? s.de : s.en}</option>)}
            </select>
          </Row>
          <Row label={de ? "Standort" : "Location"}>
            {t.location_id ? (
              <button onClick={() => go(`/locations/${encodeURIComponent(t.location_id)}`)} className="inline-flex items-center gap-1 font-mono text-amber-700 hover:underline">
                <MapPin className="w-3.5 h-3.5" /> {t.location_id}{locName[t.location_id] ? ` · ${locName[t.location_id]}` : ""}
              </button>
            ) : dash}
          </Row>
          <Row label={de ? "Kategorie" : "Category"}>{de ? cat.de : cat.en}</Row>
          <Row label={de ? "Gerät" : "Device"}>
            {t.device_code ? (
              <button onClick={() => go(`/d/${encodeURIComponent(t.device_code)}`)} className="inline-flex items-center gap-1 font-mono text-[#c00000] hover:underline">
                <Monitor className="w-3.5 h-3.5" /> {t.device_code}
              </button>
            ) : dash}
          </Row>
          <Row label={de ? "Zuständig" : "Assignee"}>
            {t.assignee ? (
              <span className="inline-flex items-center gap-1">{t.assignee_type === "group" ? <Users className="w-3.5 h-3.5" /> : <User className="w-3.5 h-3.5" />} {assigneeLabel(t, users, de)}</span>
            ) : dash}
          </Row>
          <Row label={de ? "Eingang" : "Created"}><span className="font-mono text-gray-600"><Clock className="w-3.5 h-3.5 inline mr-1" />{fmtDate(t.created_at)}</span></Row>
          <Row label={de ? "Fällig" : "Due"}>
            {t.due_date ? <span className={`font-mono ${overdue ? "text-red-700" : "text-gray-700"}`}>{fmtDateOnly(t.due_date)}{overdue ? (de ? " · überfällig" : " · overdue") : ""}</span> : dash}
          </Row>
          {(t.sla_customer_due || t.sla_internal_due) && (
            <Row label="SLA">
              <div className="flex flex-col gap-1.5">
                {t.sla_customer_due && <SlaBadge label={de ? "Kunde" : "Customer"} due={t.sla_customer_due} breached={t.sla_customer_breached} closed={t.status === "closed"} de={de} />}
                {t.sla_internal_due && <SlaBadge label={de ? "Intern" : "Internal"} due={t.sla_internal_due} breached={t.sla_internal_breached} closed={t.status === "closed"} de={de} />}
              </div>
            </Row>
          )}
          {t.ticket_type === "standort" && t.location_draft && (
            <Row label={de ? "Standort-Antrag" : "Location request"}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-[13px]">
                <div><span className="text-gray-600">{de ? "Name" : "Name"}:</span> {t.location_draft.location_name || "—"}</div>
                <div><span className="text-gray-600">{de ? "Code" : "Code"}:</span> <span className="font-mono">{t.location_draft.desired_code || (de ? "auto" : "auto")}</span></div>
                <div className="sm:col-span-2"><span className="text-gray-600">{de ? "Adresse" : "Address"}:</span> {[t.location_draft.street, [t.location_draft.zip, t.location_draft.city].filter(Boolean).join(" ")].filter(Boolean).join(", ") || "—"}</div>
                <div><span className="text-gray-600">{de ? "Kontakt" : "Contact"}:</span> {t.location_draft.contact || "—"}</div>
                <div><span className="text-gray-600">{de ? "Telefon" : "Phone"}:</span> {t.location_draft.phone || "—"}</div>
                <div><span className="text-gray-600">E-Mail:</span> {t.location_draft.email || "—"}</div>
                <div><span className="text-gray-600">{de ? "Wunschtermin" : "Date"}:</span> {t.location_draft.desired_date ? fmtDateOnly(t.location_draft.desired_date) : "—"}</div>
                {t.location_draft.hardware && <div className="sm:col-span-2"><span className="text-gray-600">{de ? "Hardware" : "Hardware"}:</span> {t.location_draft.hardware}</div>}
              </div>
            </Row>
          )}
          <Row label={de ? "Notiz" : "Note"}>{t.note ? <span className="whitespace-pre-wrap">{t.note}</span> : dash}</Row>

          {t.attachments && t.attachments.length > 0 && (
            <Row label={de ? "Anhänge" : "Attachments"}>
              <div className="flex flex-wrap gap-2" data-testid="ticket-detail-attachments">
                {t.attachments.map((att) => <AttachmentThumb key={att.id} att={att} />)}
              </div>
            </Row>
          )}

          {t.items && t.items.length > 0 && (
            <Row label={de ? "Produkte" : "Items"}>
              <div className="flex flex-wrap gap-1.5">
                {t.items.map((it, i) => (
                  <span key={i} className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border border-[#c00000]/25 bg-[#c00000]/10 text-[#c00000]"><Package className="w-3 h-3" /><span className="font-mono">{it.qty}×</span> {it.name}</span>
                ))}
              </div>
            </Row>
          )}
          {(t.shipping_status && t.shipping_status !== "offen") || t.tracking_number || t.carrier ? (
            <Row label={de ? "Versand" : "Shipping"}>
              <div className="flex flex-wrap items-center gap-1.5">
                {t.shipping_status && <span className="inline-flex items-center gap-1 text-[11px] font-mono uppercase px-1.5 py-0.5 rounded border border-sky-500/30 bg-sky-500/10 text-sky-700"><Truck className="w-3 h-3" /> {shipLabel[t.shipping_status] || t.shipping_status}</span>}
                {t.carrier && <span className="text-[11px] px-1.5 py-0.5 rounded border border-gray-200 bg-gray-50 text-gray-700">{t.carrier}</span>}
                {t.tracking_number && <span className="text-[11px] font-mono px-1.5 py-0.5 rounded border border-gray-200 bg-gray-50 text-gray-700">{t.tracking_number}</span>}
              </div>
            </Row>
          ) : null}
        </div>

        <div className="flex items-center justify-end gap-2 p-5 border-t border-gray-200 sticky bottom-0 bg-gray-50">
          {t.approval_status === "pending" && canApprove && (
            <>
              <button data-testid="ticket-reject-btn" onClick={() => decide(false)} className="flex items-center gap-2 bg-red-500/15 border border-red-500/40 text-red-700 hover:bg-red-500/25 font-semibold rounded-lg px-3 py-2 text-sm transition-colors"><XCircle className="w-4 h-4" /> {de ? "Ablehnen" : "Reject"}</button>
              <button data-testid="ticket-approve-btn" onClick={() => decide(true)} className="flex items-center gap-2 bg-[#c00000]/90 hover:bg-[#c00000] text-black font-semibold rounded-lg px-3 py-2 text-sm transition-colors"><CheckCircle2 className="w-4 h-4" /> {de ? "Freigeben" : "Approve"}</button>
            </>
          )}
          {canEdit && <button data-testid="ticket-detail-edit" onClick={() => { onEdit && onEdit(t); }} className="flex items-center gap-2 bg-amber-500/90 hover:bg-amber-500 text-black font-semibold rounded-lg px-3 py-2 text-sm transition-colors"><Pencil className="w-4 h-4" /> {de ? "Bearbeiten" : "Edit"}</button>}
          <button onClick={onClose} className="bg-gray-50 border border-gray-200 text-gray-700 rounded-lg px-3 py-2 text-sm hover:bg-gray-100 transition-colors">{de ? "Schließen" : "Close"}</button>
        </div>
      </div>
    </>
  );
}
